
import React, { useState, useMemo, useEffect } from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { SpotlightCard } from './SpotlightCard';
import { ChevronDownIcon } from './icons/ChevronDownIcon';
import { SearchIcon } from './icons/SearchIcon';
import { BookOpenIcon } from './icons/BookOpenIcon';
import { CreditCardIcon } from './icons/CreditCardIcon';
import { DesktopComputerIcon } from './icons/DesktopComputerIcon';
import { QuestionMarkCircleIcon } from './icons/QuestionMarkCircleIcon';
import { useTranslation } from '../i18n/LanguageContext';

type Category = 'Aulas' | 'Pagamento' | 'Técnico' | 'Geral' | 'Classes' | 'Payment' | 'Technical' | 'General' | 'Todas' | 'All';

interface FaqItemProps {
  question: string;
  answer: string;
  category: Category;
}

const categoryIcons = {
    'Todas': QuestionMarkCircleIcon,
    'Aulas': BookOpenIcon,
    'Pagamento': CreditCardIcon,
    'Técnico': DesktopComputerIcon,
    'Geral': QuestionMarkCircleIcon,
    'All': QuestionMarkCircleIcon,
    'Classes': BookOpenIcon,
    'Payment': CreditCardIcon,
    'Technical': DesktopComputerIcon,
    'General': QuestionMarkCircleIcon,
};

const FaqItem: React.FC<{question: string, answer: string}> = ({ question, answer }) => {
  return (
    <SpotlightCard className="bg-brand-primary/60 rounded-lg shadow-lg hover:shadow-brand-accent/10 transition-shadow duration-300">
      <details className="group p-5">
        <summary className="flex items-center justify-between cursor-pointer list-none">
          <h3 className="text-base sm:text-lg font-serif font-semibold text-brand-light">{question}</h3>
          <ChevronDownIcon className="w-5 h-5 text-brand-accent transform transition-transform duration-300 group-open:rotate-180 flex-shrink-0 ml-4" />
        </summary>
        <div className="text-brand-text mt-3 pt-3 border-t border-slate-700/50">
          <p className="text-sm leading-relaxed">{answer}</p>
        </div>
      </details>
    </SpotlightCard>
  );
};

export const FaqSection: React.FC = () => {
  const { t, language } = useTranslation();
  const allFaqItems = t('faq.items') as FaqItemProps[];
  const categories = t('faq.categories') as { name: keyof typeof categoryIcons }[];
  
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<keyof typeof categoryIcons>(categories[0]?.name);

  // Efeito para garantir que a primeira categoria ("Todas" ou "All") seja selecionada ao mudar o idioma
  useEffect(() => {
    if (categories && categories.length > 0) {
      setActiveCategory(categories[0].name);
    }
  }, [language, categories]);

  const filteredFaqs = useMemo(() => {
    return allFaqItems.filter(item => {
      const firstCategory = categories[0]?.name;
      // Se estiver na primeira categoria (Todas/All), mostra tudo
      const matchesCategory = activeCategory === firstCategory || item.category === activeCategory;
      const matchesSearch = searchTerm.trim() === '' || 
                            item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            item.answer.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchTerm, activeCategory, allFaqItems, categories]);

  return (
    <section id="faq" className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
          <SectionTitle
            title={t('faq.title')}
            subtitle={t('faq.subtitle')}
          />
        </ScrollAnimation>

        <div className="max-w-3xl mx-auto">
            <ScrollAnimation animation="slideInUp" delay={150}>
                 <div className="mb-8 space-y-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder={t('faq.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-brand-primary/80 border border-slate-600/70 rounded-lg text-brand-light focus:ring-2 focus:ring-brand-accent focus:outline-none transition-colors"
                            aria-label={t('faq.searchAriaLabel')}
                        />
                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
                    </div>
                    <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                        {categories.map(({ name }) => {
                          const Icon = categoryIcons[name];
                          return (
                            <button
                                key={name}
                                onClick={() => setActiveCategory(name)}
                                className={`flex items-center gap-2 px-3 py-1.5 text-sm sm:px-4 sm:py-2 rounded-full font-semibold transition-all duration-200 border-2
                                    ${activeCategory === name
                                        ? 'bg-brand-accent/20 border-brand-accent text-white'
                                        : 'bg-brand-primary/70 border-transparent text-slate-300 hover:bg-brand-primary hover:text-white'
                                    }`}
                            >
                                <Icon className="w-5 h-5" />
                                {name}
                            </button>
                          )
                        })}
                    </div>
                </div>
            </ScrollAnimation>

            <div className="space-y-4">
              {filteredFaqs.length > 0 ? (
                  filteredFaqs.map((item, index) => (
                    <ScrollAnimation key={`${activeCategory}-${item.question}`} animation="fadeIn" delay={index * 50} as="div">
                        <FaqItem question={item.question} answer={item.answer} />
                    </ScrollAnimation>
                  ))
              ) : (
                <ScrollAnimation animation="fadeIn" as="div">
                    <div className="text-center py-10 bg-brand-primary/60 rounded-lg border border-slate-800">
                        <p className="text-brand-text">{t('faq.noResults.title')}</p>
                        <p className="text-sm text-slate-400">{t('faq.noResults.subtitle')}</p>
                    </div>
                </ScrollAnimation>
              )}
            </div>
        </div>
      </div>
    </section>
  );
};
